import React from 'react';
import { t } from 'i18next';
import { Button } from '@brighterion/br-ui';
import useGetListNames from 'Hooks/query-hooks/list/useGetListNames';
import { resolveRuleType, handleOnClickEvent, handleKeyEvent } from 'Utils';
import TenantGroupOverview from 'Routes/ManageTenants/routes/TenantGroup/components/TenantGroupOverview';
import TenantGroupRulesTable from 'Routes/ManageTenants/routes/TenantGroup/components/TenantGroupRulesTable';
import useGetDeployedTenantGroupOverrides from 'Hooks/query-hooks/tenant-groups/useGetDeployedTenantGroupOverrides';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import useGetTenantGroupRulesColumns from 'Hooks/useGetTenantGroupRulesColumns';
import useGetParameters from 'Hooks/query-hooks/parameters/useGetParameters';
import RouteLoading from 'Components/RouteLoading';
import errorController from 'Components/ErrorScreens';

import styles from './SingleTenantGroupView.css';

type SingleTenantGroupViewProps = {
    rulesData: GlobalRulesData;
    currTenantGroup?: TenantGroup;
    setShowSingleTenantGroup: React.Dispatch<React.SetStateAction<boolean>>;
    version: number;
};

const SingleTenantGroupView = ({
    rulesData,
    currTenantGroup,
    setShowSingleTenantGroup,
    version
}: SingleTenantGroupViewProps) => {
    const { users } = useGetReduxUsers();
    const listNamesAPI = useGetListNames();
    const parametersAPI = useGetParameters({
        queryParams: {
            status: JSON.stringify(['active'])
        }
    });
    const deployedTenantGroupOverridesAPI = useGetDeployedTenantGroupOverrides({
        tenantGroupId: String(currTenantGroup?.id),
        version,
        users,
        enabled:
            !listNamesAPI.isLoading &&
            !listNamesAPI.isFetching &&
            !parametersAPI.isLoading &&
            !parametersAPI.isFetching,
        listNameMap: listNamesAPI.data,
        parameterData: parametersAPI.data
    });
    const { hasError, errorCmp } = errorController([deployedTenantGroupOverridesAPI.error]);
    const ruleTableColumns = useGetTenantGroupRulesColumns();
    const tenantGroupOverride = React.useMemo(() => {
        return {
            ...deployedTenantGroupOverridesAPI.data,
            rules: deployedTenantGroupOverridesAPI.data?.rules
                .filter((rule) => rule.inherit === 'TENANT_GROUP')
                .map((rule) => {
                    return { ...rule, ruleType: resolveRuleType(rule) };
                })
        };
    }, [deployedTenantGroupOverridesAPI.data]);

    const redirectToTenantGroupList = () => {
        setShowSingleTenantGroup(false);
    };

    if (deployedTenantGroupOverridesAPI.isLoading || deployedTenantGroupOverridesAPI.isFetching)
        return <RouteLoading />;
    if (hasError) return <>{errorCmp}</>;

    return (
        <div className={styles.rulesContainer}>
            <Button
                className={styles.tenantGroupBtn}
                variant='tertiary'
                size='small'
                iconName='arrow-left-1'
                onClick={handleOnClickEvent(redirectToTenantGroupList)}
                onKeyDown={handleKeyEvent(redirectToTenantGroupList)}
            >
                {t('Back to Tenant Groups')}
            </Button>

            <TenantGroupOverview
                // @ts-ignore
                ruleData={tenantGroupOverride}
                numericTenantGroupId={currTenantGroup?.id}
                tenantGroup={currTenantGroup}
                mode='revisionWindow'
            />
            <div className={styles.tableContainer}>
                <TenantGroupRulesTable
                    columnNames={ruleTableColumns}
                    globalRules={rulesData}
                    // @ts-ignore
                    tenantOverrideRules={tenantGroupOverride!}
                    mode='revisionWindow'
                />
            </div>
        </div>
    );
};

export default SingleTenantGroupView;
